import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';

type Post = Database['public']['Tables']['posts']['Row'];

export interface PostWithProfile extends Post {
  profiles: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    company: string | null;
    role: string | null;
    headline: string | null;
  } | null;
  post_media?: {
    id: string;
    storage_path: string;
    file_name: string;
    file_type: string;
  }[];
}

const CHANNEL_PAGE_SIZE = 100;

export function useChannelPosts(channelId?: string) {
  return useQuery({
    queryKey: ['channel-posts', channelId],
    queryFn: async (): Promise<PostWithProfile[]> => {
      if (!channelId) return [];

      // Get posts for this channel
      const { data: posts, error: postsError } = await supabase
        .from('posts')
        .select('*')
        .eq('channel_id', channelId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .limit(CHANNEL_PAGE_SIZE);

      if (postsError) throw postsError;
      if (!posts || posts.length === 0) return [];

      // Get unique user IDs
      const userIds = [...new Set(posts.map((p) => p.user_id))];

      const postIds = posts.map((p) => p.id);
      const [profilesRes, mediaRes] = await Promise.all([
        userIds.length > 0
          ? supabase
              .from('profiles')
              .select('id, user_id, full_name, avatar_url, company, role, headline')
              .in('user_id', userIds)
          : Promise.resolve({ data: [], error: null }),
        postIds.length > 0
          ? supabase
              .from('post_media')
              .select('id, post_id, storage_path, file_name, file_type')
              .in('post_id', postIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (profilesRes.error) throw profilesRes.error;
      if (mediaRes.error) throw mediaRes.error;

      const profiles = profilesRes.data || [];
      const media = mediaRes.data || [];

      // Create maps
      const profileMap = new Map(profiles.map((p) => [p.user_id, p]));
      const mediaMap = new Map<string, typeof media>();
      media?.forEach((m) => {
        const existing = mediaMap.get(m.post_id) || [];
        existing.push(m);
        mediaMap.set(m.post_id, existing);
      });

      // Merge posts with profiles and media
      return posts.map((post) => {
        const profile = profileMap.get(post.user_id);
        return {
          ...post,
          profiles: profile
            ? {
                id: profile.id,
                full_name: profile.full_name,
                avatar_url: profile.avatar_url,
                company: profile.company,
                role: profile.role,
                headline: profile.headline,
              }
            : null,
          post_media: mediaMap.get(post.id) || [],
        };
      });
    },
    staleTime: 1000 * 30,
    enabled: !!channelId,
  });
}

export function useCohortPosts(cohortTag?: string) {
  return useQuery({
    queryKey: ['cohort-posts', cohortTag],
    queryFn: async (): Promise<PostWithProfile[]> => {
      if (!cohortTag) return [];

      // Get posts for this cohort by cohort_tag (case-insensitive)
      const { data: posts, error: postsError } = await supabase
        .from('posts')
        .select('*')
        .ilike('cohort_tag', cohortTag)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .limit(CHANNEL_PAGE_SIZE);

      if (postsError) throw postsError;
      if (!posts || posts.length === 0) return [];

      // Get unique user IDs
      const userIds = [...new Set(posts.map((p) => p.user_id))];

      const postIds = posts.map((p) => p.id);
      const [profilesRes, mediaRes] = await Promise.all([
        userIds.length > 0
          ? supabase
              .from('profiles')
              .select('id, user_id, full_name, avatar_url, company, role, headline')
              .in('user_id', userIds)
          : Promise.resolve({ data: [], error: null }),
        postIds.length > 0
          ? supabase
              .from('post_media')
              .select('id, post_id, storage_path, file_name, file_type')
              .in('post_id', postIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (profilesRes.error) throw profilesRes.error;
      if (mediaRes.error) throw mediaRes.error;

      const profiles = profilesRes.data || [];
      const media = mediaRes.data || [];

      // Create maps
      const profileMap = new Map(profiles.map((p) => [p.user_id, p]));
      const mediaMap = new Map<string, typeof media>();
      media?.forEach((m) => {
        const existing = mediaMap.get(m.post_id) || [];
        existing.push(m);
        mediaMap.set(m.post_id, existing);
      });

      // Merge posts with profiles and media
      return posts.map((post) => {
        const profile = profileMap.get(post.user_id);
        return {
          ...post,
          profiles: profile
            ? {
                id: profile.id,
                full_name: profile.full_name,
                avatar_url: profile.avatar_url,
                company: profile.company,
                role: profile.role,
                headline: profile.headline,
              }
            : null,
          post_media: mediaMap.get(post.id) || [],
        };
      });
    },
    staleTime: 1000 * 30,
    enabled: !!cohortTag,
  });
}

export function useChannelPostsPaged(params: {
  channelId?: string;
  page: number;
  pageSize: number;
}) {
  return useQuery({
    queryKey: ['channel-posts', 'paged', params],
    queryFn: async (): Promise<{ data: PostWithProfile[]; count: number }> => {
      if (!params.channelId) return { data: [], count: 0 };

      const from = params.page * params.pageSize;
      const to = from + params.pageSize - 1;

      const { data: posts, error: postsError, count } = await supabase
        .from('posts')
        .select('*', { count: 'exact' })
        .eq('channel_id', params.channelId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (postsError) throw postsError;
      if (!posts || posts.length === 0) return { data: [], count: count || 0 };

      const userIds = [...new Set(posts.map((p) => p.user_id))];
      const postIds = posts.map((p) => p.id);
      const [profilesRes, mediaRes] = await Promise.all([
        userIds.length > 0
          ? supabase
              .from('profiles')
              .select('id, user_id, full_name, avatar_url, company, role, headline')
              .in('user_id', userIds)
          : Promise.resolve({ data: [], error: null }),
        postIds.length > 0
          ? supabase
              .from('post_media')
              .select('id, post_id, storage_path, file_name, file_type')
              .in('post_id', postIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (profilesRes.error) throw profilesRes.error;
      if (mediaRes.error) throw mediaRes.error;

      const profiles = profilesRes.data || [];
      const media = mediaRes.data || [];

      const profileMap = new Map(profiles.map((p) => [p.user_id, p]));
      const mediaMap = new Map<string, typeof media>();
      media?.forEach((m) => {
        const existing = mediaMap.get(m.post_id) || [];
        existing.push(m);
        mediaMap.set(m.post_id, existing);
      });

      const merged = posts.map((post) => {
        const profile = profileMap.get(post.user_id);
        return {
          ...post,
          profiles: profile
            ? {
                id: profile.id,
                full_name: profile.full_name,
                avatar_url: profile.avatar_url,
                company: profile.company,
                role: profile.role,
                headline: profile.headline,
              }
            : null,
          post_media: mediaMap.get(post.id) || [],
        };
      });

      return { data: merged, count: count || 0 };
    },
    staleTime: 1000 * 30,
    enabled: !!params.channelId,
  });
}

export function useCohortPostsPaged(params: {
  cohortTag?: string;
  page: number;
  pageSize: number;
}) {
  return useQuery({
    queryKey: ['cohort-posts', 'paged', params],
    queryFn: async (): Promise<{ data: PostWithProfile[]; count: number }> => {
      if (!params.cohortTag) return { data: [], count: 0 };

      const from = params.page * params.pageSize;
      const to = from + params.pageSize - 1;

      const { data: posts, error: postsError, count } = await supabase
        .from('posts')
        .select('*', { count: 'exact' })
        .ilike('cohort_tag', params.cohortTag)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (postsError) throw postsError;
      if (!posts || posts.length === 0) return { data: [], count: count || 0 };

      const userIds = [...new Set(posts.map((p) => p.user_id))];
      const postIds = posts.map((p) => p.id);
      const [profilesRes, mediaRes] = await Promise.all([
        userIds.length > 0
          ? supabase
              .from('profiles')
              .select('id, user_id, full_name, avatar_url, company, role, headline')
              .in('user_id', userIds)
          : Promise.resolve({ data: [], error: null }),
        postIds.length > 0
          ? supabase
              .from('post_media')
              .select('id, post_id, storage_path, file_name, file_type')
              .in('post_id', postIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (profilesRes.error) throw profilesRes.error;
      if (mediaRes.error) throw mediaRes.error;

      const profiles = profilesRes.data || [];
      const media = mediaRes.data || [];

      const profileMap = new Map(profiles.map((p) => [p.user_id, p]));
      const mediaMap = new Map<string, typeof media>();
      media?.forEach((m) => {
        const existing = mediaMap.get(m.post_id) || [];
        existing.push(m);
        mediaMap.set(m.post_id, existing);
      });

      const merged = posts.map((post) => {
        const profile = profileMap.get(post.user_id);
        return {
          ...post,
          profiles: profile
            ? {
                id: profile.id,
                full_name: profile.full_name,
                avatar_url: profile.avatar_url,
                company: profile.company,
                role: profile.role,
                headline: profile.headline,
              }
            : null,
          post_media: mediaMap.get(post.id) || [],
        };
      });

      return { data: merged, count: count || 0 };
    },
    staleTime: 1000 * 30,
    enabled: !!params.cohortTag,
  });
}

export function useCreateChannelPost() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      content,
      channelId,
      category = 'discussion',
    }: {
      content: string;
      channelId: string;
      category?: 'referral' | 'discussion' | 'plug';
    }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('posts')
        .insert({
          user_id: user.id,
          content,
          channel_id: channelId,
          category,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, { channelId }) => {
      queryClient.invalidateQueries({ queryKey: ['channel-posts', channelId] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}
